const getStockBadge = ({
  isVariantLowInStock,
  isVariantInStock,
  isProductInStock,
  isRestockingSoon
}) => {
  const isOutOfStock = isVariantInStock === false || isProductInStock === false;

  if (isOutOfStock) {
    if (isRestockingSoon) return "Restocking Soon";

    return "Out of Stock";
  }

  if (isVariantLowInStock) return "Low in Stock";

  return null;
};

const buildBadgesArray = ({
  isProductSellingFast,
  earlyAccess,
  isHotProduct,
  isVariantLowInStock,
  isVariantInStock,
  isProductInStock,
  isRestockingSoon,
  discount
}) => {
  const stockBadge = getStockBadge({
    isVariantLowInStock,
    isVariantInStock,
    isProductInStock,
    isRestockingSoon
  });

  const badgeConditions = [
    { condition: isProductSellingFast, label: "Selling Fast" },
    { condition: earlyAccess?.customerAccess, label: "Early Access" },
    { condition: isHotProduct, label: "Hot Product" },
    { condition: discount > 0, label: "Sale" },
    { condition: stockBadge, label: stockBadge }
  ];

  const badges = badgeConditions
    .filter(({ condition }) => condition)
    .map(({ label }) => label);

  return badges.length > 0 ? badges : undefined;
};

const getSize = (variants, variantId) => {
  const selectedVariant = variants?.find(({ id }) => id === variantId);

  return selectedVariant ? selectedVariant.size : undefined;
};

const buildItemObject = ({
  productId,
  productCode,
  variantId,
  title,
  brand,
  price,
  colour,
  variants,
  index
}) => {
  const xrp = price?.xrp;
  const current = price?.current;

  return {
    productId: productId ? String(productId) : undefined,
    productCode: String(productCode),
    variantId: variantId ? String(variantId) : undefined,
    item_name: title,
    index: index + 1,
    item_brand: brand,
    price: xrp?.value || undefined,
    priceLocalCurrency: current?.value,
    quantity: 1,
    size: getSize(variants, variantId),
    item_variant: colour || undefined,
    google_business_vertical: "retail"
  };
};

const addBadges = (
  item,
  {
    isProductSellingFast,
    earlyAccess,
    isHotProduct,
    isVariantLowInStock,
    isVariantInStock,
    isProductInStock,
    isRestockingSoon,
    discount
  }
) => {
  const badges = buildBadgesArray({
    isProductSellingFast,
    earlyAccess,
    isHotProduct,
    isVariantLowInStock,
    isVariantInStock,
    isProductInStock,
    isRestockingSoon,
    discount
  });

  if (badges) {
    item.badges = badges;
  }
};

const addStockProperties = (
  item,
  {
    isProductSellingFast,
    isVariantLowInStock,
    isVariantInStock,
    isProductInStock
  }
) => {
  if (isProductSellingFast !== undefined)
    item.isSellingFast = isProductSellingFast;
  if (isVariantLowInStock !== undefined)
    item.isLowInStock = isVariantLowInStock;
  if (isVariantInStock !== undefined) item.isInStock = isVariantInStock;
  if (isProductInStock !== undefined) item.isProductInStock = isProductInStock;
};

const addDiscountProperties = (item, price) => {
  const { wasPrice, current } = price || {};
  if (wasPrice?.value && current?.value && wasPrice.value > current.value) {
    item.hasDiscount = true;
    item.discountPercentage = Math.round(
      ((wasPrice.value - current.value) / wasPrice.value) * 100
    );
  }
};

const addProductFlags = (item, { earlyAccess, isRestockingSoon }) => {
  if (earlyAccess?.customerAccess)
    item.earlyAccess = earlyAccess.customerAccess;
  if (isRestockingSoon !== undefined) item.isRestockingSoon = isRestockingSoon;
};

const itemDataTemplate = (
  {
    productId,
    productCode,
    variantId,
    brand,
    colour,
    title,
    price,
    variants,
    source,
    seller,
    isProductSellingFast,
    isVariantLowInStock,
    isVariantInStock,
    isProductInStock,
    earlyAccess,
    isHotProduct,
    isRestockingSoon,
    discount
  },
  index = 0
) => {
  const item = buildItemObject({
    productId,
    productCode,
    variantId,
    brand,
    colour,
    title,
    price,
    variants,
    index
  });

  const sourceId = source?.id ?? null;
  const sellerId = seller?.id ?? null;

  item.flexibleSalesChannels = {
    sourceId,
    sellerId
  };

  addStockProperties(item, {
    isProductSellingFast,
    isVariantLowInStock,
    isVariantInStock,
    isProductInStock
  });
  addDiscountProperties(item, price);
  addProductFlags(item, { earlyAccess, isRestockingSoon });

  const effectiveDiscount = discount ?? item.discountPercentage;

  addBadges(item, {
    isProductSellingFast,
    earlyAccess,
    isHotProduct,
    isVariantLowInStock,
    isVariantInStock,
    isProductInStock,
    isRestockingSoon,
    discount: effectiveDiscount
  });

  return item;
};

export default itemDataTemplate;
